// Thin wrapper around fetch that always sends cookies, parses JSON, and
// attaches a CSRF token on state-changing requests. Per CLAUDE.md every HTTP
// call lives behind try/catch with structured logging, and we never use axios.

const CSRF_TOKEN_PATH = '/api/csrf-token';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  public constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

interface ApiOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

let cachedCsrfToken: string | null = null;
let inFlightTokenFetch: Promise<string> | null = null;

async function ensureCsrfToken(): Promise<string> {
  // `null` means we haven't fetched yet; empty string means "server has no
  // CSRF endpoint, don't send a header" (cached to avoid re-fetching the 404).
  if (cachedCsrfToken !== null) return cachedCsrfToken;
  inFlightTokenFetch ??= fetchCsrfToken();
  cachedCsrfToken = await inFlightTokenFetch;
  inFlightTokenFetch = null;
  return cachedCsrfToken;
}

const HTTP_NOT_FOUND = 404;

async function fetchCsrfToken(): Promise<string> {
  const response = await fetch(CSRF_TOKEN_PATH, { credentials: 'include' });
  // Two equivalent "no CSRF enforced" signals:
  //   - 404 (endpoint not mounted, older server)
  //   - 200 with { token: '' } (endpoint mounted but middleware off in dev)
  // Either way we return empty string; apiFetch skips the header when the
  // cached token is empty.
  if (response.status === HTTP_NOT_FOUND) return '';
  if (!response.ok) throw new ApiError(response.status, 'Could not fetch CSRF token', null);
  const body = (await response.json()) as { token?: string };
  if (typeof body.token !== 'string') {
    throw new ApiError(response.status, 'CSRF token missing in response', body);
  }
  return body.token;
}

// Tests reset cached state between runs.
export function resetCsrfCache(): void {
  cachedCsrfToken = null;
  inFlightTokenFetch = null;
}

export async function apiFetch<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (!SAFE_METHODS.has(method)) {
    const token = await ensureCsrfToken();
    if (token) headers[CSRF_HEADER] = token;
  }
  const init: RequestInit = { method, credentials: 'include', headers };
  if (options.body !== undefined) init.body = JSON.stringify(options.body);
  if (options.signal) init.signal = options.signal;

  const response = await fetch(path, init);
  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const body: unknown = text ? safeParseJson(text) : null;
  if (!response.ok) {
    throw new ApiError(response.status, `Request failed with ${String(response.status)}`, body);
  }
  return body as T;
}

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
